export type Route = '/' | '/pdf1' | '/pdf2' | '/pdf3' | '/pdf4' | '/pdf5' | '/pdf6' | '/pdf7' | '/pdf1/view' | '/pdf2/view' | '/pdf3/view' | '/pdf4/view' | '/pdf5/view' | '/pdf6/view' | '/pdf7/view';

/**
 * Type for routes that may include query parameters
 * Examples: '/pdf1/view?file=doc.pdf', '/pdf1'
 */
export type RouteWithParams = Route | `${Route}?${string}`;

export interface RouterConfig {
  routes: Route[];
  defaultRoute: Route;
}

export class Router {
  private currentRoute: Route = '/';
  private routeHandlers: Map<Route, () => void> = new Map();
  private config: RouterConfig;

  constructor(config: RouterConfig) {
    this.config = config;
    this.setupEventListeners();
    // Don't handle initial route immediately - let the app register handlers first
  }

  private setupEventListeners(): void {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      const path = window.location.pathname as Route;
      const baseRoute = this.stripQueryParams(path);
      this.currentRoute = baseRoute;
      this.handleRouteChange(baseRoute);
    });

    // Note: Removed 'load' event listener to prevent duplicate route handling
    // Initial route is handled by router.start() after handlers are registered
  }

  /**
   * Handles the initial route when the application starts
   * Validates the current pathname and navigates to it or the default route
   * @private
   */
  private handleInitialRoute(): void {
    const fullPath = window.location.pathname + window.location.search;
    const path = window.location.pathname as Route;
    if (this.config.routes.includes(path)) {
      this.navigate(fullPath as RouteWithParams, false);
    } else {
      this.navigate(this.config.defaultRoute, false);
    }
  }

  /**
   * Navigates to a specified route
   * Accepts routes with or without query parameters (e.g., '/pdf1/view?file=doc.pdf')
   * @param route - The route to navigate to, optionally with query parameters
   * @param updateHistory - Whether to update browser history (default: true)
   * @public
   */
  public navigate(route: RouteWithParams, updateHistory: boolean = true): void {
    // Strip query parameters for route validation
    const baseRoute = this.stripQueryParams(route);

    if (!this.config.routes.includes(baseRoute)) {
      console.warn(`Invalid route: ${route}`);
      return;
    }

    this.currentRoute = baseRoute;

    if (updateHistory) {
      // Preserve full route with query params in history
      window.history.pushState({ route }, '', route);
    }

    // Handle route change with base route (without query params)
    this.handleRouteChange(baseRoute);
  }

  private handleRouteChange(route: Route): void {
    const handler = this.routeHandlers.get(route);
    if (handler) {
      handler();
    } else {
      console.warn(`No handler found for route: ${route}`);
    }
  }

  /**
   * Strips query parameters from a route for validation purposes
   * @param route - The full route including query params (e.g., '/pdf1/view?file=doc.pdf')
   * @returns The base route without query parameters (e.g., '/pdf1/view')
   * @private
   */
  private stripQueryParams(route: RouteWithParams): Route {
    const questionMarkIndex = route.indexOf('?');
    if (questionMarkIndex === -1) {
      return route as Route;
    }
    return route.substring(0, questionMarkIndex) as Route;
  }

  /**
   * Registers a handler for a specific route
   * @param route - The route to register the handler for
   * @param handler - The function to call when the route is activated
   * @public
   */
  public on(route: Route, handler: () => void): void {
    this.routeHandlers.set(route, handler);
  }

  /**
   * Gets the current active route (without query parameters)
   * @returns The current route
   * @public
   */
  public getCurrentRoute(): Route {
    return this.currentRoute;
  }

  /**
   * Gets the query parameters from the current URL
   * @returns URLSearchParams object containing all query parameters
   * @public
   * @example
   * // URL: /pdf1/view?file=doc.pdf&page=2
   * const params = router.getQueryParams();
   * params.get('file'); // 'doc.pdf'
   * params.get('page'); // '2'
   */
  public getQueryParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  /**
   * Navigates back in browser history
   * @public
   */
  public goBack(): void {
    window.history.back();
  }

  /**
   * Navigates forward in browser history
   * @public
   */
  public goForward(): void {
    window.history.forward();
  }

  /**
   * Starts the router and handles the initial route
   * Should be called after all route handlers are registered
   * @public
   */
  public start(): void {
    // Handle initial route after handlers are registered
    this.handleInitialRoute();
  }
}

// Create and export router instance
export const router = new Router({
  routes: ['/', '/pdf1', '/pdf2', '/pdf3', '/pdf4', '/pdf5', '/pdf6', '/pdf7', '/pdf1/view', '/pdf2/view', '/pdf3/view', '/pdf4/view', '/pdf5/view', '/pdf6/view', '/pdf7/view'],
  defaultRoute: '/'
});
